"use client";
import { zodResolver } from "@hookform/resolvers/zod";
import React from "react";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { Button } from "src/components/buttons/shadcn-button";
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from "src/components/form/form";
import { Input } from "src/components/input/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "src/components/input/Select";
import { addNewHire } from "src/redux/api/_hr.api";
import { NewHireFormValues, NewHireSchema } from "src/schemas/add-new-hire";

const hiringTypes = [
  "Full-Time Office",
  "Part-Time Office",
  "Temporary Office",
  "Contractual Office",
  "Seasonal Office",
  "Remote Full-Time",
  "Remote Part-Time",
  "Remote Contractual",
  "Remote Temporary",
  "Hybrid Full-Time",
  "Hybrid Part-Time",
  "Hybrid Contractual",
  "Hybrid Temporary",
];

export default function OnboardingForm({refetch}) {
  const form = useForm<NewHireFormValues>({
    resolver: zodResolver(NewHireSchema),
  });

  const onSubmit = async (values: NewHireFormValues) => {
    try {
      const response = await addNewHire(values);

      if(response.message.toLowerCase() === "success") {
        toast.success("New Hire Added Successfully!");
        refetch();
      };

    } catch (error) {
      toast.error("Failed to Add New Hire!");
      console.error(error);
    }
  };

  return (
    <div className="bg-card shadow-lg rounded-xl p-6 space-y-4 border border-gray-200">
      <h3 className="text-xl font-semibold text-gray-800">Add New Hire</h3>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <div className="md:grid grid-cols-2 gap-4">
            {/* Full Name */}
            <FormField
              control={form.control}
              name="full_name"
              rules={{ required: "Name is required" }}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Full Name</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter full name" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Email */}
            <FormField
              control={form.control}
              name="email"
              rules={{
                required: "Email is required",
                pattern: {
                  value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                  message: "Invalid email address",
                },
              }}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email</FormLabel>
                  <FormControl>
                    <Input type="email" placeholder="Enter Email" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="contact_number"
              rules={{
                required: "Contact is required",
                pattern: {
                  value: /^[0-9]{10}$/,
                  message: "Enter a valid 10-digit number",
                },
              }}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Contact Number</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter contact number" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Address */}
            <FormField
              control={form.control}
              name="address"
              rules={{ required: "Address is required" }}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Address</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter address" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div className="md:grid grid-cols-3 gap-4 my-4">
            {/* Position */}
            <FormField
              control={form.control}
              name="position"
              rules={{ required: "Position is required" }}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Position</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter position" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="employment_type"
              rules={{ required: "Employement type is required" }}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Hiring Type</FormLabel>
                  <Select
                    onValueChange={field.onChange}
                    defaultValue={field.value}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select hiring type" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent className="rounded-xl">
                      {hiringTypes.map((type) => (
                        <SelectItem
                          value={type}
                          key={type}
                          className="focus:bg-accent focus:text-foreground"
                        >
                          {type}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Letter Type */}
            <FormField
              control={form.control}
              name="letter_type"
              rules={{ required: "Letter type is required" }}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Letter Type</FormLabel>
                  <Select
                    onValueChange={field.onChange}
                    defaultValue={field.value}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select letter type" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent className="rounded-xl">
                      {["Offer Letter", "Appointment Letter"].map((type) => (
                        <SelectItem
                          value={type}
                          key={type}
                          className="focus:bg-accent focus:text-foreground"
                        >
                          {type}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          {/* Submit Button */}
          <div className="flex justify-center items-center mt-6">
            <Button className="px-12" disabled={form.formState.isSubmitting}>
              {" "}
              {form.formState.isSubmitting ? (
                <span className="loading loading-dots loading-md"></span>
              ) : (
                "Save New Hire"
              )}{" "}
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
}
